import React, { useEffect, useId, useRef, useState } from 'react';
import './AppSelect.css';

/** Ignore a second press that was queued while the page was busy painting. */
const TRIGGER_GUARD_MS = 350;

export default function AppSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select',
  closedLabel,
  disabled = false,
  className = '',
  id,
  minWidth,
  'aria-label': ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const fallbackId = useId();
  const selectId = id || fallbackId;
  const openedAtRef = useRef(0);
  const pointerHandledRef = useRef(false);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));
  const displayLabel = closedLabel || selectedOption?.label || placeholder;

  useEffect(() => {
    if (!open) return undefined;

    const onDocPointerDown = (event) => {
      // The press that opened the menu, and a duplicate press queued behind a
      // long render, must not immediately close it.
      if (performance.now() - openedAtRef.current < TRIGGER_GUARD_MS) return;
      if (rootRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    // Attach after this press finishes so the opening event cannot close it.
    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', onDocPointerDown, true);
    }, 0);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggleOpen = () => {
    if (disabled) return;
    const now = performance.now();
    setOpen((current) => {
      if (!current) {
        openedAtRef.current = now;
        return true;
      }
      // A second press queued behind a long render should leave the menu open.
      if (now - openedAtRef.current < TRIGGER_GUARD_MS) return true;
      return false;
    });
  };

  const handleSelect = (nextValue) => {
    onChange?.(nextValue);
    setOpen(false);
  };

  const runPointerAction = (event, action) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    pointerHandledRef.current = true;
    action();
  };

  const runClickFallback = (event, action) => {
    event.stopPropagation();
    if (event.detail !== 0 && pointerHandledRef.current) {
      pointerHandledRef.current = false;
      return;
    }
    pointerHandledRef.current = false;
    action();
  };

  return (
    <div
      ref={rootRef}
      className={[
        'app-select',
        open ? 'is-open' : '',
        disabled ? 'is-disabled' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={minWidth ? { minWidth } : undefined}
    >
      <button
        type="button"
        id={selectId}
        className="app-select__trigger"
        onPointerDown={(event) => runPointerAction(event, toggleOpen)}
        onClick={(event) => runClickFallback(event, toggleOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className={`app-select__value${closedLabel || selectedOption ? '' : ' is-placeholder'}`}>
          {displayLabel}
        </span>
        <i className="ti ti-chevron-down app-select__chevron" aria-hidden="true" />
      </button>
      {open && (
        <ul
          className="app-select__menu"
          role="listbox"
          aria-labelledby={selectId}
          onPointerDown={(event) => event.stopPropagation()}
        >
          {options.map((opt) => {
            const isSelected = String(opt.value) === String(value);
            const actions = Array.isArray(opt.actions) ? opt.actions : [];
            return (
              <li
                key={String(opt.value)}
                role="presentation"
                className={actions.length ? 'app-select__option-row' : undefined}
              >
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`app-select__option${isSelected ? ' is-selected' : ''}`}
                  onPointerDown={(event) => runPointerAction(event, () => handleSelect(opt.value))}
                  onClick={(event) => runClickFallback(event, () => handleSelect(opt.value))}
                >
                  {opt.label}
                </button>
                {actions.length > 0 && (
                  <span className="app-select__option-actions">
                    {actions.map((action) => (
                      <button
                        key={action.key}
                        type="button"
                        className={`app-select__option-action${action.danger ? ' is-danger' : ''}`}
                        aria-label={action.label}
                        title={action.label}
                        onPointerDown={(event) => runPointerAction(event, () => {
                          setOpen(false);
                          action.onClick?.();
                        })}
                        onClick={(event) => runClickFallback(event, () => {
                          setOpen(false);
                          action.onClick?.();
                        })}
                      >
                        <i className={`ti ${action.icon}`} aria-hidden="true" />
                      </button>
                    ))}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
